/**
 * Noise screening (framework-aligned).
 *
 * Noise screening over a Forma-native noise analysis, normalized to the
 * canonical `simulation-evidence/v1` contract so it flows through the same
 * report path as engine-produced evidence. Output is technical screening
 * evidence only and never asserts municipal compliance.
 */

import type {
  EvidenceFinding,
  EvidenceMetric,
  EvidenceStatus,
  SimulationEvidence,
} from './contracts';

import type {
  FormaAnalysisGroundGrid,
  FormaAnalysisRecord,
  FormaNoiseAnalysis,
  FormaNoiseAnalysisParameters,
  NoiseAnalysisResult,
} from './index';

const SCREENING_ONLY_FINDING: EvidenceFinding = {
  code: 'screening-only',
  severity: 'info',
  message: 'Output is technical screening evidence only and does not assert municipal compliance.',
};

/** Noise screening over a Forma-native noise analysis, normalized as evidence. */
export function analyzeNoiseScreening(result: NoiseAnalysisResult): SimulationEvidence {
  const parameters = isFormaNoiseAnalysis(result.analysis) ? result.analysis.parameters : undefined;
  const missingInputs = buildNoiseMissingInputs(result, parameters);
  const isBlocked =
    result.analysis.status === 'FAILED' ||
    missingInputs.includes('noiseAnalysisType') ||
    missingInputs.includes('noiseParameters');
  const status: EvidenceStatus = isBlocked
    ? 'blocked'
    : result.analysis.status !== 'SUCCEEDED' || missingInputs.length > 0
      ? 'needs-review'
      : 'screening-ready';

  return {
    schemaVersion: 'simulation-evidence/v1',
    studyId: 'noise',
    source: 'forma-native',
    status,
    confidence:
      result.analysis.status === 'SUCCEEDED' && missingInputs.length === 0
        ? 'high'
        : result.analysis.status === 'FAILED' || missingInputs.includes('noiseParameters')
          ? 'low'
          : 'medium',
    metrics: buildNoiseMetrics(result.groundGrid, parameters),
    findings: buildNoiseFindings(result, missingInputs),
    missingInputs,
  };
}

function isFormaNoiseAnalysis(analysis: FormaAnalysisRecord): analysis is FormaNoiseAnalysis {
  return (
    analysis.analysisType === 'noise' &&
    typeof analysis.parameters === 'object' &&
    analysis.parameters !== null
  );
}

function buildNoiseMissingInputs(
  result: NoiseAnalysisResult,
  parameters?: FormaNoiseAnalysisParameters,
): string[] {
  const missingInputs: string[] = [];

  if (result.analysis.analysisType !== 'noise') {
    missingInputs.push('noiseAnalysisType');
  }

  if (result.analysis.status === 'FAILED') {
    missingInputs.push('succeededNoiseAnalysis');
  }

  if (parameters === undefined) {
    missingInputs.push('noiseParameters');

    return missingInputs;
  }

  if (!parameters.hasRoad && !parameters.hasRail) {
    missingInputs.push('trafficSource');
  }

  if (result.groundGrid === undefined) {
    missingInputs.push('noiseGroundGrid');
  }

  return missingInputs;
}

function buildNoiseMetrics(
  groundGrid?: FormaAnalysisGroundGrid,
  parameters?: FormaNoiseAnalysisParameters,
): EvidenceMetric[] {
  const metrics: EvidenceMetric[] = [
    { label: 'Selected elements', value: parameters?.selectedElementPaths.length ?? 0 },
  ];

  if (parameters !== undefined) {
    metrics.push({ label: 'Has road', value: parameters.hasRoad ? 'yes' : 'no' });
    metrics.push({ label: 'Has rail', value: parameters.hasRail ? 'yes' : 'no' });
    metrics.push({ label: 'Resolution', value: parameters.groundResolution, unit: 'm' });
  }

  if (groundGrid === undefined) {
    return metrics;
  }

  metrics.push({ label: 'Grid cells', value: groundGrid.width * groundGrid.height });

  const values = Array.from(groundGrid.grid).filter(
    (_value, index) => groundGrid.mask?.[index] !== 0,
  );

  if (values.length > 0) {
    metrics.push({
      label: 'Mean noise level',
      value: roundTwo(values.reduce((sum, value) => sum + value, 0) / values.length),
      unit: 'dB',
    });
    metrics.push({ label: 'Minimum noise level', value: Math.min(...values), unit: 'dB' });
    metrics.push({ label: 'Maximum noise level', value: Math.max(...values), unit: 'dB' });
  }

  return metrics;
}

function buildNoiseFindings(
  result: NoiseAnalysisResult,
  missingInputs: readonly string[],
): EvidenceFinding[] {
  const findings: EvidenceFinding[] = [SCREENING_ONLY_FINDING];

  if (result.analysis.status !== 'SUCCEEDED') {
    findings.push({
      code: 'noise-analysis-status',
      severity: result.analysis.status === 'FAILED' ? 'blocker' : 'warning',
      message: `Forma noise analysis status is ${result.analysis.status}.`,
    });
  }

  for (const input of missingInputs) {
    findings.push({
      code: `missing-${input}`,
      severity: input === 'noiseGroundGrid' || input === 'trafficSource' ? 'warning' : 'blocker',
      message: `Missing noise screening input: ${input}.`,
    });
  }

  return findings;
}

function roundTwo(value: number): number {
  return Math.round(value * 100) / 100;
}
